import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { BoardTemplate } from '../../mainApi';
import { TemplateUpdateInput } from '../../services/templates.service';
import { getTemplateDescriptionText } from './templateDescription';

type EditTemplateDialogProps = {
  template: BoardTemplate | null;
  isUpdating: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: TemplateUpdateInput) => void;
};

export const EditTemplateDialog = ({
  template,
  isUpdating,
  error,
  onClose,
  onSubmit,
}: EditTemplateDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!template) {
      return;
    }

    setName(template.name);
    setDescription(getTemplateDescriptionText(template.description));
    setNameError('');
  }, [template]);

  const handleSubmit = () => {
    const trimmedName = name.trim();

    setNameError('');

    if (!trimmedName) {
      setNameError('Required');
      return;
    }

    onSubmit({
      name: trimmedName,
      description: description.trim(),
    });
  };

  return (
    <Dialog
      open={Boolean(template)}
      onClose={() => {
        if (!isUpdating) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Template name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (nameError) {
                setNameError('');
              }
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            disabled={isUpdating}
            autoFocus
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={isUpdating}
            fullWidth
            multiline
            minRows={4}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
